import { type ReactNode, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Box, CircularProgress, Typography, Button, Alert } from '@mui/material';
import { useProjectAuthorization, useEpisodeAuthorization } from '../../hooks/useAuthorization';
import { ROUTES } from '../../config/constants';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

/**
 * AuthorizedRoute - Protects routes that require resource ownership
 * 
 * This is a UX enhancement - real security is on the backend!
 * Prevents users from seeing unauthorized content and provides better error messages.
 * 
 * Usage:
 * <AuthorizedRoute resourceType="project" resourceIdParam="id">
 *   <YourComponent />
 * </AuthorizedRoute>
 */

interface AuthorizedRouteProps {
  children: ReactNode;
  resourceType: 'project' | 'episode';
  resourceIdParam?: string; // URL param name for resource ID (defaults to 'id')
  projectIdParam?: string; // URL param name for project ID (for episodes)
  fallbackPath?: string; // Where to redirect if unauthorized
  showUnauthorizedMessage?: boolean; // Show error message vs silent redirect
}

export const AuthorizedRoute = ({
  children,
  resourceType,
  resourceIdParam = 'id',
  projectIdParam = 'projectId',
  fallbackPath,
  showUnauthorizedMessage = true,
}: AuthorizedRouteProps) => {
  const navigate = useNavigate();
  const params = useParams();

  // Extract IDs from URL params
  const resourceId = params[resourceIdParam] ? Number(params[resourceIdParam]) : undefined;
  const projectId = params[projectIdParam] ? Number(params[projectIdParam]) : undefined;

  // Determine which authorization check to use
  const projectAuth = useProjectAuthorization(
    resourceType === 'project' ? resourceId : projectId
  );
  const episodeAuth = useEpisodeAuthorization(
    resourceType === 'episode' ? projectId : undefined,
    resourceType === 'episode' ? resourceId : undefined
  );

  const auth = resourceType === 'project' ? projectAuth : episodeAuth;
  const { isAuthorized, isLoading, error } = auth;

  // Redirect if not authorized (after loading completes)
  useEffect(() => {
    if (!isLoading && !isAuthorized && !showUnauthorizedMessage) {
      const redirectPath = fallbackPath || ROUTES.PROJECTS;
      navigate(redirectPath, { replace: true });
    }
  }, [isLoading, isAuthorized, showUnauthorizedMessage, navigate, fallbackPath]);

  // Show loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Verifying access...
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        gap={3}
        px={2}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="body1" gutterBottom>
            Unable to verify access to this resource
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error.message}
          </Typography>
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(fallbackPath || ROUTES.PROJECTS)}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  // Show unauthorized message
  if (!isAuthorized && showUnauthorizedMessage) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        gap={3}
        px={2}
        textAlign="center"
      >
        <LockIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
        <Typography variant="h5" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" maxWidth={500}>
          {resourceType === 'project'
            ? "You don't have permission to access this project. It may not exist or belong to another user."
            : "You don't have permission to access this episode. It may not exist or belong to another user's project."}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(fallbackPath || ROUTES.PROJECTS)}
          sx={{ mt: 2 }}
        >
          {resourceType === 'project' ? 'Back to Projects' : 'Back to Projects'}
        </Button>
      </Box>
    );
  }

  // User is authorized - render children
  if (isAuthorized) {
    return <>{children}</>;
  }

  // Fallback (shouldn't reach here, but just in case)
  return null;
};

/**
 * Simpler component-level authorization check
 * Use when you want to conditionally render content based on ownership
 * 
 * Usage:
 * <AuthorizedContent projectId={123}>
 *   <Button>Delete Project</Button>
 * </AuthorizedContent>
 */
interface AuthorizedContentProps {
  children: ReactNode;
  projectId?: number;
  episodeId?: number;
  fallback?: ReactNode;
  hideOnUnauthorized?: boolean; // If true, renders nothing when unauthorized
}

export const AuthorizedContent = ({
  children,
  projectId,
  episodeId,
  fallback = null,
  hideOnUnauthorized = false,
}: AuthorizedContentProps) => {
  const projectAuth = useProjectAuthorization(projectId);
  const episodeAuth = useEpisodeAuthorization(projectId, episodeId);

  // Determine which auth to use
  const auth = episodeId ? episodeAuth : projectAuth;
  const { isAuthorized, isLoading } = auth;

  // While loading, show nothing or fallback
  if (isLoading) {
    return hideOnUnauthorized ? null : <>{fallback}</>;
  }

  // If authorized, show children
  if (isAuthorized) {
    return <>{children}</>;
  }

  // If unauthorized, show fallback or nothing
  return hideOnUnauthorized ? null : <>{fallback}</>;
};
